import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { PawPrint, Heart, Stethoscope, ShieldCheck, Sparkles, Users, Calendar, MessageCircle, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link, useLocation } from 'react-router-dom';
import { IMAGES, CONTACT } from '../data/mock';
import { cdnImg } from '../utils/cdn';
import { trackCallClick, trackWhatsAppClick, trackBookButtonClick } from '../lib/analytics';

const TITLE = 'About Ichi & Ori Pet Care | Our Story | Veterinary Clinic in Noida';
const DESCRIPTION = 'Learn the story behind Ichi & Ori Pet Care, a premium veterinary clinic in Sector 90, Noida built by passionate pet parents committed to compassionate, transparent veterinary care.';

const PHILOSOPHY = [
  { Icon: Heart,       title: 'Treat every pet like our own', text: 'Every recommendation we make is the same one we’d make if your pet were our own.' },
  { Icon: ShieldCheck, title: 'Honesty before business',      text: 'We’ll always recommend what is best for your pet, even when it isn’t the most expensive option.' },
  { Icon: Sparkles,    title: 'Gentle, low-stress care',      text: 'We believe compassion is just as important as clinical expertise.' },
  { Icon: Users,       title: 'Relationships that last',      text: 'We hope to care for your pets through every stage of their lives, not just during illness.' },
];

const set = (sel, attr, val) => { const el = document.head.querySelector(sel); if (el) el.setAttribute(attr, val); };

const About = () => {
  const location = useLocation();
  useEffect(() => {
    const orig = document.head.querySelector('meta[name="description"]')?.getAttribute('content');
    set('meta[name="description"]', 'content', DESCRIPTION);
    return () => { if (orig != null) set('meta[name="description"]', 'content', orig); };
  }, []);
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      requestAnimationFrame(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [location.hash]);

  const wa = `https://wa.me/${CONTACT.phoneRaw.replace(/\D/g, '')}`;

  return (
    <div>
      <Helmet>
        <title>{TITLE}</title>
      </Helmet>

      {/* 1 — HERO (replaces PageHeader / removes breadcrumb) */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#fbe6f5] via-[#faf5fb] to-white py-24 sm:py-28 lg:py-32">
        <PawPrint className="absolute top-16 left-10 w-24 h-24 text-[#e870b6]/10 rotate-12" aria-hidden="true" />
        <PawPrint className="absolute bottom-16 right-16 w-32 h-32 text-[#5b3eb5]/10 -rotate-6" aria-hidden="true" />
        <div className="relative max-w-[900px] mx-auto px-5 lg:px-10 text-center">
          <span className="inline-block text-[11px] sm:text-xs font-bold tracking-[0.32em] text-[#5b3eb5] mb-5">OUR PURPOSE</span>
          <h1 className="font-serif text-[42px] sm:text-[56px] lg:text-[72px] font-extrabold text-[#1a0b3d] leading-[1.05] mb-6">
            Why Ichi &amp; Ori Exists
          </h1>
          <p className="text-[17px] sm:text-lg text-[#3b1d76]/85 leading-[1.7] max-w-[680px] mx-auto">
            More than a veterinary hospital. A place built by pet parents who believe every animal deserves the same love, dignity and care as family.
          </p>
        </div>
      </section>

      {/* 2 — TRANSITION before the story */}
      <section className="bg-white py-20 sm:py-24 lg:py-28">
        <div className="max-w-[760px] mx-auto px-5 lg:px-10 text-center">
          <h2 className="font-serif text-[30px] sm:text-[38px] lg:text-[44px] font-extrabold text-[#1a0b3d] leading-[1.12] mb-8">
            Every great clinic begins with a reason.
          </h2>
          <p className="text-[#3b1d76]/85 text-base sm:text-[18px] leading-[1.85]">
            Ours wasn&apos;t born from a business plan.
          </p>
          <p className="text-[#3b1d76]/85 text-base sm:text-[18px] leading-[1.85] mt-4">
            It began with years of loving animals, experiencing loss, learning difficult lessons, and believing pet parents deserved a different kind of veterinary experience.
          </p>
        </div>
      </section>

      {/* 3 — OUR STORY (founder narrative — text preserved exactly) */}
      <section id="our-story" className="py-20 lg:py-28 bg-[#1a0b3d] text-white relative overflow-hidden scroll-mt-32">
        <PawPrint className="absolute top-10 right-10 w-32 h-32 text-white/5" aria-hidden="true" />
        <PawPrint className="absolute bottom-20 left-10 w-24 h-24 text-white/5 rotate-45" aria-hidden="true" />

        <div className="max-w-[1100px] mx-auto px-5 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-flex items-center gap-2 text-[#e870b6] font-semibold tracking-[0.2em] text-[13px] uppercase mb-4">
              <PawPrint size={16} /> Our Story
            </span>
            <h2 className="font-serif text-[40px] lg:text-[56px] font-extrabold leading-[1.1]">
              Our Story: A Legacy of Love and Care
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-6 mb-14">
            {[IMAGES.story1, IMAGES.story3].map((src, i) => (
              <div key={i} className="rounded-2xl overflow-hidden aspect-square shadow-2xl group bg-white/5">
                <img src={cdnImg(src, { w: 800 })} alt={`Ichi and Ori family memory ${i + 1}`} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
            ))}
          </div>

          <div className="max-w-[720px] mx-auto space-y-8 text-[17px] lg:text-[18px] leading-[1.85] text-white/85">
            <p>
              At <span className="text-[#e870b6] font-semibold">Ichi and Ori Pet Care</span>, our journey is deeply personal&mdash;a story of love, loss, and an unwavering commitment to the well-being of pets.
            </p>
            <p>
              For over two decades, I have been a devoted pet parent, raising and loving my pets as my own children. Through this journey, I faced countless challenges&mdash;ones that every pet parent experiences. I learned, grew, and made mistakes along the way, always striving to give my pets the best life possible.
            </p>
            <p>
              Then, life took an unexpected turn. In the span of just six months, I lost three of the most precious beings in my life&mdash;my beloved father, my cockatoo <span className="text-[#f3d6f5] font-semibold">Zazu</span>, and my dog <span className="text-[#f3d6f5] font-semibold">Ichi</span>. Their loss left an unfillable void, and suddenly, I was left with only <span className="text-[#f3d6f5] font-semibold">Ori</span>, my last remaining baby.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-6 my-14">
            {[IMAGES.story4, IMAGES.story2].map((src, i) => (
              <div key={i} className="rounded-2xl overflow-hidden aspect-square shadow-2xl group bg-white/5">
                <img src={cdnImg(src, { w: 800 })} alt={`Ichi and Ori family memory ${i + 3}`} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
            ))}
          </div>

          <div className="max-w-[720px] mx-auto space-y-8 text-[17px] lg:text-[18px] leading-[1.85] text-white/85">
            <p>
              Through my grief, I found purpose. My father, the ultimate animal lover, had always dreamed of a world where pets received the same love and care as family. Inspired by his devotion and my own experiences as a pet parent, I decided to turn my pain into something meaningful&mdash;to create a pet care experience that truly understands and supports both pets and their parents.
            </p>
            <p>
              Thus, <span className="text-[#e870b6] font-semibold">Ichi and Ori Pet Care</span> was born&mdash;a tribute to the ones I lost and a promise to every pet and pet parent who walks through our doors. Here, we don&rsquo;t just provide veterinary care, grooming, day boarding, and premium retail&mdash;we create a sanctuary where pets are cherished, and pet parents feel understood.
            </p>
            <p>
              This is more than a business. It is my heartfelt mission to ensure that no pet parent feels alone in their journey and that every pet receives the love, comfort, and care they deserve.
            </p>
            <p className="text-[#f3d6f5] font-serif text-[20px] lg:text-[22px] font-semibold pt-6 border-t border-white/15 mt-6">
              Welcome to Ichi and Ori Pet Care&mdash;where pets are family, and their happiness is our purpose.
            </p>
          </div>
        </div>
      </section>

      {/* 4 — OUR PHILOSOPHY (new, 4 cards) */}
      <section className="bg-white py-20 sm:py-24 lg:py-28">
        <div className="max-w-[1200px] mx-auto px-5 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block text-[11px] sm:text-xs font-bold tracking-[0.32em] text-[#5b3eb5] mb-4">OUR PHILOSOPHY</span>
            <h2 className="font-serif text-[32px] sm:text-[40px] lg:text-[48px] font-extrabold text-[#1a0b3d] leading-[1.1]">
              The standard we hold ourselves to
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PHILOSOPHY.map((p, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl p-7 sm:p-8 border border-[#f3d6f5]/60 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col h-full"
              >
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#fdf3fc] to-[#f3d6f5] flex items-center justify-center mb-5 flex-shrink-0">
                  <p.Icon className="w-5 h-5 text-[#5b3eb5]" />
                </div>
                <h3 className="font-serif text-[20px] sm:text-[22px] font-bold text-[#1a0b3d] mb-3 leading-snug">{p.title}</h3>
                <p className="text-slate-600 text-[15px] leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5 — EXISTING VALUES + NEW CTA */}
      <section className="bg-white pb-20 sm:pb-24 lg:pb-28">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-10">
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {[
              { Icon: Heart, title: 'Heartfelt Care', text: 'Every pet is treated like family with genuine compassion.' },
              { Icon: Stethoscope, title: 'Expert Team', text: 'Trained, licensed professionals devoted to pet wellness.' },
              { Icon: PawPrint, title: 'Safe Sanctuary', text: 'Calming, clean and stress-free environment for every paw.' },
            ].map(({ Icon, title, text }) => (
              <div key={title} className="bg-white rounded-2xl p-8 border border-[#f3d6f5]/50 shadow-md hover:shadow-2xl transition-all hover:-translate-y-1 flex flex-col h-full">
                <div className="w-14 h-14 rounded-full bg-[#f3d6f5] flex items-center justify-center mb-5 flex-shrink-0">
                  <Icon className="w-7 h-7 text-[#5b3eb5]" />
                </div>
                <h3 className="font-serif text-[22px] font-bold text-[#1a0b3d] mb-3">{title}</h3>
                <p className="text-slate-600 text-[15px] leading-relaxed">{text}</p>
              </div>
            ))}
          </div>

          {/* New CTA */}
          <div className="bg-gradient-to-br from-[#5b3eb5] to-[#1a0b3d] rounded-3xl p-10 lg:p-14 text-white text-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-25 [background-image:radial-gradient(circle_at_20%_30%,#e870b6_0%,transparent_45%)]" />
            <PawPrint className="absolute top-5 right-5 w-24 h-24 text-white/10 rotate-12" aria-hidden="true" />
            <PawPrint className="absolute bottom-5 left-5 w-20 h-20 text-white/10 -rotate-12" aria-hidden="true" />
            <div className="relative">
              <h2 className="font-serif text-[30px] sm:text-[38px] lg:text-[46px] font-extrabold mb-4 leading-[1.1]">
                We&apos;d love to meet you and your pet.
              </h2>
              <p className="text-white/85 mb-8 max-w-xl mx-auto text-base sm:text-lg leading-relaxed">
                Whether it&apos;s your puppy&apos;s first visit, a routine health check or expert advice when you&apos;re worried, we&apos;re here to help.
              </p>
              <div className="flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-4">
                <Link to="/book" onClick={() => trackBookButtonClick('about_final')} className="w-full sm:w-auto">
                  <Button className="w-full sm:w-auto rounded-full bg-[#f084c4] hover:bg-[#e870b6] text-white px-8 py-6 h-auto text-[15px] font-semibold gap-2 shadow-lg hover:shadow-2xl hover:scale-[1.02] sm:hover:scale-105 transition-all">
                    <Calendar className="w-4 h-4" /> Book a Visit
                  </Button>
                </Link>
                <a href={wa} target="_blank" rel="noreferrer" onClick={() => trackWhatsAppClick('about_final')} className="w-full sm:w-auto">
                  <Button className="w-full sm:w-auto rounded-full bg-[#3ddc7e] hover:bg-[#25D366] text-white px-8 py-6 h-auto text-[15px] font-semibold gap-2 shadow-lg hover:shadow-2xl hover:scale-[1.02] sm:hover:scale-105 transition-all">
                    <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
                  </Button>
                </a>
                <a href={`tel:${CONTACT.phoneRaw}`} onClick={() => trackCallClick('about_final')} className="w-full sm:w-auto sm:ml-1">
                  <span className="inline-flex items-center gap-2 text-white/85 hover:text-white font-medium text-[14px] sm:text-[15px] underline decoration-white/30 underline-offset-4 hover:decoration-white transition-colors px-1 py-2">
                    <Phone className="w-4 h-4" /> Call {CONTACT.phone}
                  </span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
